/** Turning a failed request into an error a user can read.
 *
 * Four call sites had grown their own copy of this — Agents, Skills, Chat
 * connectors, file download — and all four built the message the same way, so
 * all four printed the same nonsense when the request never reached the server:
 * `undefined: undefined` (#11634).
 *
 * That happens whenever `fetch` itself rejects rather than returning a
 * response. A load-balancer-generated 502/504 does it, because the error page
 * the ALB writes carries no `Access-Control-Allow-Origin` and the API is a
 * different origin from the app, so the browser refuses the response outright.
 * `fpFetch` catches the rejection and returns a stand-in in its place. The
 * stand-in used to be a bare `{ ok: false }`, so interpolating its status and
 * status text gave two `undefined`s. It now reports both, and this is the one
 * place that turns either kind of failure into a message.
 */

/** The status `fpFetch` reports when there was no response at all.
 *
 * `0` is the long-standing convention for this (XHR has used it since forever),
 * it is falsy so `if (!status)` reads naturally, and no real HTTP status can
 * collide with it. Kept here rather than in `fpFetch` because that module is
 * mocked in a dozen test files, and every named export it gains has to be
 * restated in each of those mocks. */
export const NETWORK_ERROR_STATUS = 0;

/** The stand-in detail used when the server said nothing usable.
 *
 * Named rather than inlined because `userMessageOf` has to recognise it: it
 * means "no explanation available", so a caller that has a better sentence for
 * the action being attempted ("Failed to save template") should win over it.
 * On HTTP/2 an empty `statusText` is the common case, so this is the message
 * most 5xx failures would otherwise show. */
export const GENERIC_FAILURE_DETAIL = "Request failed";

/** What this needs off a failed response.
 *
 * Structural rather than `Response`, because `fpFetch` returns either a real
 * Response or its plain-object stand-in. `status` is required, not optional: a
 * fallback here would mean the four call sites no longer depend on `fpFetch`
 * reporting one, which is exactly what made an earlier version of this fix
 * untestable — a broken stand-in was masked and every test stayed green. */
export type FailedResponse = {
  status: number;
  statusText?: string;
  json?: () => Promise<unknown>;
};

export type ApiError = Error & { status: number };

function networkErrorMessage(): string {
  // The two cases have different remedies, and the browser already knows which
  // one this is — so say which rather than making the user guess. Only the
  // `false` reading is trusted: a browser reporting itself online can still be
  // behind a captive portal or a dead upstream, but one reporting itself
  // offline has no route at all.
  if (navigator.onLine === false) {
    return "You appear to be offline. Check your connection and try again.";
  }
  return "The server could not be reached. It may be temporarily unavailable — please try again.";
}

/** Pull the human sentences out of django-ninja's validation-failure body.
 *
 * A failed `field_validator` does not answer with `detail` as a string — ninja
 * returns the pydantic error list verbatim, so `detail` is
 * `[{type, loc, msg, ctx}, …]` and the sentence the validator author wrote is
 * in `msg`. Callers that interpolated `detail` straight into a message showed
 * users `[object Object]`, which is what a CFF user stared at for three minutes
 * while her template refused to save (#11787).
 *
 * Returns null rather than an empty string when there is nothing to say, so the
 * caller falls back to the status text instead of rendering `"422: "`. */
function messagesFromValidationDetail(detail: unknown[]): string | null {
  const messages = detail
    .map((entry) => {
      if (!entry || typeof entry !== "object") return "";
      const { msg, ctx } = entry as { msg?: unknown; ctx?: unknown };

      // `ctx.error` is the validator's own message. Ninja stringifies whatever
      // the validator raised into it, so it arrives clean — whereas `msg` is
      // pydantic's rendering of the same thing with a "Value error, " prefix
      // glued on. Prefer the structured field; matching an English framework
      // string is a bet that pydantic never rewords it.
      if (typeof ctx === "object" && ctx !== null) {
        const { error } = ctx as { error?: unknown };
        if (typeof error === "string" && error) return error;
      }
      return typeof msg === "string" ? msg.replace(/^Value error,\s*/, "") : "";
    })
    .filter(Boolean);

  // Every failing field, not just the first — otherwise fixing one reveals the
  // next only on the following save. Sentence-separated because pydantic
  // messages carry no terminal punctuation, so joining on a space runs them
  // together into one unreadable line.
  return messages.length > 0 ? messages.join(". ") : null;
}

function detailFromBody(body: unknown, fallback: string): string {
  if (body && typeof body === "object") {
    const { detail, message } = body as { detail?: unknown; message?: unknown };
    if (typeof detail === "string" && detail) return detail;
    if (Array.isArray(detail)) {
      // Fall through rather than return: a body carrying an unusable `detail`
      // and a usable `message` should still yield the message.
      const messages = messagesFromValidationDetail(detail);
      if (messages) return messages;
    }
    if (typeof message === "string" && message) return message;
  }
  return fallback;
}

/** Build the error to throw for a response that came back `ok: false`.
 *
 * Real HTTP errors keep the `"<status>: <detail>"` shape they have always had:
 * `sendErrorMessage` strips that prefix with a regex, and callers branch on the
 * attached `status` rather than parsing the text. Only the no-response case
 * changes, because it was the only one that had nothing to say. */
export async function errorFromResponse(
  response: FailedResponse
): Promise<ApiError> {
  const { status } = response;

  if (status === NETWORK_ERROR_STATUS) {
    return Object.assign(new Error(networkErrorMessage()), {
      status: NETWORK_ERROR_STATUS,
    });
  }

  // Browsers report `statusText: ""` for every HTTP/2 response, so this is the
  // common path in production rather than an edge case. "Request failed" is a
  // deliberately plain stand-in: repeating the code ("502: HTTP 502") stutters
  // wherever the caller does not strip the prefix.
  let detail = response.statusText || GENERIC_FAILURE_DETAIL;
  try {
    detail = detailFromBody(await response.json?.(), detail);
  } catch {
    // Body wasn't JSON; the status text stands.
  }

  return Object.assign(new Error(`${status}: ${detail}`), { status });
}

/** The text to put in front of a user for a failed request.
 *
 * `errorFromResponse` builds `"<status>: <detail>"` because callers have long
 * relied on that shape, but the status code is noise on screen — it tells the
 * user nothing they can act on and pushes the part that matters to the right.
 * Anything rendering an error into the UI should go through this. */
export function userMessageOf(err: unknown, fallback: string): string {
  const message = err instanceof Error ? err.message : "";
  const withoutStatus = message.replace(/^\d{3}:\s*/, "").trim();
  // The generic stand-in is not an explanation, so the caller's action-named
  // fallback beats it. Without this, an empty-bodied 5xx — the common shape on
  // HTTP/2 — would tell the user "Request failed" and not what failed.
  if (!withoutStatus || withoutStatus === GENERIC_FAILURE_DETAIL)
    return fallback;
  return withoutStatus;
}

/** The HTTP status an `ApiError` carries, when it has one.
 *
 * `errorFromResponse` attaches it precisely so callers can branch on the status
 * without parsing the message — a 402 credit block, a 404 expired task. Lives
 * here beside the thing that sets it. */
export function statusOf(err: unknown): number | undefined {
  return err && typeof err === "object" && "status" in err
    ? (err as { status?: number }).status
    : undefined;
}
